import './BankingInformationWidget.scss';

import React, { useCallback } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { putJSON } from 'api/fetch';
import NewText from 'fe-design-base/atoms/Text';
import Alert from 'fe-design-base/molecules/Alert';
import PropTypes from 'prop-types';

import {
  getCanManageCompletedDocuments,
  getCurrentUserId,
  getIsPayrollEnrolled,
} from 'selectors/session';

import {
  accountNumberPublicKey,
  routingNumberPublicKey,
} from 'features/employeeOnboarding/constants';

import Box from 'components/Box';
import { TeamSectionTitle } from 'components/Text';

import { cxHelpers } from 'util/className';
import { encryptWithPublicKey } from 'util/encryption';
import { error as flashError } from 'util/flashNotice';
import { toI18n } from 'util/i18n';
import { editButtonAttrs } from 'util/profile';
import { bankAccountDataValidator } from 'util/validators';

import EditableSection from '../EditableSection';
import FieldRenderer from '../FieldRenderer';

const { cx, cxEl } = cxHelpers('BankingInformationWidget');

const ACCOUNT_TYPE_OPTIONS = ['checking', 'savings'].map(value => ({
  value,
  label: toI18n(
    `team.employee_profile.banking_information.account_type_options.${value}`
  ),
}));

const BANK_NUMBER_INPUT_PROPS = {
  allowNegative: false,
  decimalSeparator: false,
  allowLeadingZeros: true,
};

const ROUTING_NUMBER_INPUT_PROPS = {
  ...BANK_NUMBER_INPUT_PROPS,
  maxLength: 9,
};

const CHECK_ERROR_MSG_KEY = /Response Error:/g;

const validate = values =>
  bankAccountDataValidator({
    decrypted_routing_number: values.decrypted_routing_number,
    decrypted_account_number: values.decrypted_account_number,
    decrypted_reentered_account_number:
      values.decrypted_reentered_account_number,
    bank_account_type: values.bank_account_type,
  });

const BankingInformationWidget = ({
  user,
  editable,
  updateUserInfo,
  mobile,
  currentUserId,
  teamDrawerMode = false,
  initEditMode,
  saveButtonProps,
  isPayrollEnrolled,
}) => {
  const handleSave = useCallback(
    values => {
      const payload = {
        encrypted_routing_number: encryptWithPublicKey(
          routingNumberPublicKey(),
          values.decrypted_routing_number
        ),
        encrypted_account_number: encryptWithPublicKey(
          accountNumberPublicKey(),
          values.decrypted_account_number
        ),
        subtype: values.bank_account_type,
      };

      return putJSON(
        `/employees/${user.get('id')}/update_banking_information.json`,
        {
          bank_account: payload,
        }
      );
    },
    [user]
  );

  const formatCheckErrorResponse = errorObj =>
    errorObj.errors.replace(CHECK_ERROR_MSG_KEY, '');

  const handleError = useCallback(error => {
    error.response.json().then(errorObj => {
      flashError(
        `${toI18n(
          'team.employee_profile.banking_information.check_error'
        )} ${formatCheckErrorResponse(errorObj)}`
      );
    });
  }, []);

  const accountTypeSavedValue = user.get('bank_account_type')
    ? toI18n(
        `team.employee_profile.banking_information.account_type_options.${user.get(
          'bank_account_type'
        )}`
      )
    : null;

  // edit button is always shown for payroll locations
  const cantEdit =
    !isPayrollEnrolled &&
    (!!user.get('decrypted_account_number') ||
      !!user.get('decrypted_routing_number'));

  const currentProfile = {
    userId: user.get('id'),
    userFullName: user.get('first_name'),
    moreThanOneCompany: user.get('has_jobs_at_more_than_one_company'),
    informationType: 'banking',
    disableBankAccountEditButton: !user.get('enabled_bank_account_edit_button'),
  };

  const editButton = editButtonAttrs(
    currentProfile,
    currentUserId,
    isPayrollEnrolled
  );

  return (
    <EditableSection
      noBorder={teamDrawerMode}
      noPadding={teamDrawerMode}
      className={cx()}
      titleI18n={
        teamDrawerMode
          ? null
          : 'team.employee_profile.banking_information.title'
      }
      onSave={handleSave}
      onError={handleError}
      updateUserInfo={updateUserInfo}
      onValidate={validate}
      initialValues={{}}
      cantEdit={teamDrawerMode || !editable || cantEdit}
      editButtonDisabled={editButton.disabled}
      editButtonContent={editButton.content}
      cxEl={cxEl}
      initEditMode={initEditMode}
      showCancelButton={!teamDrawerMode}
      saveButtonProps={saveButtonProps}
    >
      {({ editMode }) => (
        <>
          <Box row vcenter>
            <Box w={mobile ? '40%' : '20%'}>
              <TeamSectionTitle i18n="team.employee_profile.banking_information.routing_number" />
            </Box>
            <Box w={mobile ? '60%' : '80%'}>
              <Box w={mobile ? '100%' : '48%'}>
                <FieldRenderer
                  editMode={editMode}
                  name="decrypted_routing_number"
                  savedValue={user.get('decrypted_routing_number')}
                  component="TextInput"
                  type="number"
                  inputProps={ROUTING_NUMBER_INPUT_PROPS}
                />
              </Box>
            </Box>
          </Box>

          <Box row mt12 vcenter>
            <Box w={mobile ? '40%' : '20%'}>
              <TeamSectionTitle i18n="team.employee_profile.banking_information.bank_account_number" />
            </Box>
            <Box w={mobile ? '60%' : '80%'}>
              <Box w={mobile ? '100%' : '48%'}>
                <FieldRenderer
                  editMode={editMode}
                  component="TextInput"
                  name="decrypted_account_number"
                  savedValue={user.get('decrypted_account_number')}
                  type="number"
                  inputProps={BANK_NUMBER_INPUT_PROPS}
                />
              </Box>
            </Box>
          </Box>

          {editMode && (
            <Box row mt12 vcenter>
              <Box w={mobile ? '40%' : '20%'}>
                <TeamSectionTitle i18n="team.employee_profile.banking_information.reenter_bank_account_number" />
              </Box>
              <Box w={mobile ? '60%' : '80%'}>
                <Box w={mobile ? '100%' : '48%'}>
                  <FieldRenderer
                    editMode={editMode}
                    component="TextInput"
                    name="decrypted_reentered_account_number"
                    type="number"
                    inputProps={BANK_NUMBER_INPUT_PROPS}
                  />
                </Box>
              </Box>
            </Box>
          )}

          <Box row mt12 vcenter>
            <Box w={mobile ? '40%' : '20%'}>
              <TeamSectionTitle i18n="team.employee_profile.banking_information.account_type" />
            </Box>
            <Box w={mobile ? '60%' : '80%'}>
              <Box w={mobile ? '100%' : '48%'}>
                <FieldRenderer
                  editMode={editMode}
                  name="bank_account_type"
                  savedValue={accountTypeSavedValue}
                  component="SelectInput"
                  options={ACCOUNT_TYPE_OPTIONS}
                  hideClear
                />
              </Box>
            </Box>
          </Box>

          {user.get('has_pending_bank_account') && (
            <Box row mt12 vcenter>
              <Alert variant="warning">
                <NewText variant="heading4" color="mono900">
                  {toI18n(
                    'team.employee_profile.banking_information.direct_deposit_delay_warning.title'
                  )}
                </NewText>
                <NewText variant="body" color="mono900">
                  {toI18n(
                    'team.employee_profile.banking_information.direct_deposit_delay_warning.body',
                    {
                      props: {
                        last_4: user.get('pending_bank_account_last_4'),
                        direct_deposit_delay_next_pay_date: user.get(
                          'direct_deposit_delay_next_pay_date'
                        ),
                      },
                    }
                  )}
                </NewText>
              </Alert>
            </Box>
          )}
        </>
      )}
    </EditableSection>
  );
};

BankingInformationWidget.propTypes = {
  user: ImmutablePropTypes.map.isRequired,
  editable: PropTypes.bool.isRequired,
  teamDrawerMode: PropTypes.bool,
  initEditMode: PropTypes.bool,
  currentUserId: PropTypes.number.isRequired,
  saveButtonProps: PropTypes.object,
};

export default connect(state => ({
  editable: getCanManageCompletedDocuments(state),
  currentUserId: getCurrentUserId(state),
  isPayrollEnrolled: getIsPayrollEnrolled(state),
}))(BankingInformationWidget);
